import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchClusterConfigs, createClusterConfig, updateClusterConfig } from '../store/slices/configSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiPlus, FiEdit2, FiSave, FiX, FiTrash2, FiServer } from 'react-icons/fi';

// Validation schema for cluster
const ClusterSchema = Yup.object().shape({
  id: Yup.string()
    .min(3, 'Too Short!')
    .max(50, 'Too Long!')
    .matches(/^[a-zA-Z0-9-_]+$/, 'Can only contain letters, numbers, hyphens, and underscores')
    .required('Cluster ID is required'),
  name: Yup.string()
    .min(3, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Cluster name is required'),
  url: Yup.string()
    .url('Enter a valid URL')
    .required('URL is required'),
  region: Yup.string()
    .required('Region is required'),
  status: Yup.string()
    .oneOf(['active', 'maintenance', 'deprecated'], 'Invalid status')
    .required('Status is required'),
  capacity: Yup.object().shape({
    maxTenants: Yup.number()
      .required('Max tenants is required')
      .positive('Must be a positive number'),
    currentTenants: Yup.number()
      .required('Current tenants is required')
      .min(0, 'Cannot be negative')
  })
});

const ConfigurationsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { clusterConfigs, loading, error } = useSelector((state: RootState) => state.configs);
  const [creatingCluster, setCreatingCluster] = useState(false);
  const [editingClusterId, setEditingClusterId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchClusterConfigs());
  }, [dispatch]);

  const initialClusterValues = {
    id: '',
    name: '',
    url: '',
    region: '',
    status: 'active',
    capacity: {
      maxTenants: 100,
      currentTenants: 0
    }
  };

  const handleCreateCluster = async (values: any, { resetForm }: any) => {
    try {
      await dispatch(createClusterConfig(values)).unwrap();
      
      dispatch(
        addNotification({
          type: 'success',
          message: `Cluster "${values.name}" was successfully created.`,
        })
      );
      
      resetForm();
      setCreatingCluster(false);
    } catch (error: any) {
      dispatch(
        addNotification({
          type: 'error',
          title: 'Cluster Creation Failed',
          message: error.message || 'Failed to create cluster configuration.',
        })
      );
    }
  };

  const handleUpdateCluster = async (values: any, { resetForm }: any) => {
    try {
      await dispatch(updateClusterConfig({
        id: values.id,
        configData: {
          name: values.name,
          url: values.url,
          region: values.region,
          status: values.status,
          capacity: values.capacity
        }
      })).unwrap();
      
      dispatch(
        addNotification({
          type: 'success',
          message: `Cluster "${values.name}" was successfully updated.`,
        })
      );
      
      setEditingClusterId(null);
    } catch (error: any) {
      dispatch(
        addNotification({
          type: 'error',
          title: 'Update Failed',
          message: error.message || 'Failed to update cluster configuration.',
        })
      );
    }
  };

  const handleDeleteCluster = (clusterId: string, clusterName: string) => {
    dispatch(
      showModal({
        title: 'Confirm Deletion',
        content: `Are you sure you want to delete cluster "${clusterName}"? This can have serious implications if tenants are assigned to this cluster.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        onConfirm: async () => {
          // In a real implementation, you would have an API endpoint to delete clusters
          // For now, just show a notification
          dispatch(
            addNotification({
              type: 'info',
              title: 'Not Implemented',
              message: `Deletion of clusters is not implemented in this demo. Cluster "${clusterName}" was not deleted.`,
            })
          );
        },
      })
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Cluster Configurations</h1>
        
        {!creatingCluster && (
          <button
            onClick={() => setCreatingCluster(true)}
            className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <FiPlus className="mr-2" />
            New Cluster
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Create/Edit Cluster Form */}
      {(creatingCluster || editingClusterId) && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
            {creatingCluster ? 'Create New Cluster' : 'Edit Cluster'}
          </h2>
          
          <Formik
            initialValues={
              creatingCluster 
                ? initialClusterValues 
                : clusterConfigs.find(c => c.clusterId === editingClusterId) || initialClusterValues
            }
            validationSchema={ClusterSchema}
            onSubmit={creatingCluster ? handleCreateCluster : handleUpdateCluster}
          >
            {({ isValid, dirty, values, setFieldValue }) => (
              <Form className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  {/* Cluster ID */}
                  <div className="sm:col-span-3">
                    <label htmlFor="id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cluster ID
                    </label>
                    <div className="mt-1">
                      <Field
                        type="text"
                        name="id"
                        id="id"
                        disabled={!creatingCluster}
                        className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md ${
                          !creatingCluster ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                    </div>
                    <ErrorMessage name="id" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* Cluster Name */}
                  <div className="sm:col-span-3">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Cluster Name
                    </label>
                    <div className="mt-1">
                      <Field
                        type="text"
                        name="name"
                        id="name"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      />
                    </div>
                    <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* URL */}
                  <div className="sm:col-span-3">
                    <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      URL
                    </label>
                    <div className="mt-1">
                      <Field
                        type="text"
                        name="url"
                        id="url"
                        placeholder="https://example.com"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      />
                    </div>
                    <ErrorMessage name="url" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* Region */}
                  <div className="sm:col-span-3">
                    <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Region
                    </label>
                    <div className="mt-1">
                      <Field
                        type="text"
                        name="region"
                        id="region"
                        placeholder="westus"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      />
                    </div>
                    <ErrorMessage name="region" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* Status */}
                  <div className="sm:col-span-3">
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </label>
                    <div className="mt-1">
                      <Field
                        as="select"
                        name="status"
                        id="status"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      >
                        <option value="active">Active</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="deprecated">Deprecated</option>
                      </Field>
                    </div>
                    <ErrorMessage name="status" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* Max Tenants */}
                  <div className="sm:col-span-3">
                    <label htmlFor="capacity.maxTenants" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Max Tenants
                    </label>
                    <div className="mt-1">
                      <Field
                        type="number"
                        name="capacity.maxTenants"
                        id="capacity.maxTenants"
                        min="1"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      />
                    </div>
                    <ErrorMessage name="capacity.maxTenants" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>

                  {/* Current Tenants */}
                  <div className="sm:col-span-3">
                    <label htmlFor="capacity.currentTenants" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Current Tenants
                    </label>
                    <div className="mt-1">
                      <Field
                        type="number"
                        name="capacity.currentTenants"
                        id="capacity.currentTenants"
                        min="0"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                      />
                    </div>
                    <ErrorMessage name="capacity.currentTenants" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCreatingCluster(false);
                      setEditingClusterId(null);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <FiX className="mr-2" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!(isValid && dirty) || loading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                  >
                    <FiSave className="mr-2" />
                    {loading ? 'Saving...' : 'Save Cluster'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {/* Clusters List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Cluster
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  URL
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Region
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Capacity
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading && !clusterConfigs.length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                    </div>
                  </td>
                </tr>
              ) : clusterConfigs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No clusters found. Create your first cluster to get started.
                  </td>
                </tr>
              ) : (
                clusterConfigs.map((cluster) => (
                  <tr key={cluster.clusterId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FiServer className="flex-shrink-0 mr-2 h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{cluster.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{cluster.clusterId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.url}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        cluster.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : cluster.status === 'maintenance'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {cluster.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {cluster.capacity.currentTenants} / {cluster.capacity.maxTenants}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingClusterId(cluster.clusterId)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          title="Edit cluster"
                        >
                          <FiEdit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteCluster(cluster.clusterId, cluster.name)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete cluster"
                        >
                          <FiTrash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationsPage;